import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Image,
    Flex,
    TextButton,
    Grid,
    Spacer,
    Text,
} from '@zillow/constellation';
import { Center } from '@zillow/zrs-comet';
import { ZillowMapControl, ZillowMapLayer, PropertyMarker } from '@zillow/zillow-map-control';

import getBoundingRect from 'app/shared/utils/getBoundingRect';
import EnvSelectors from 'app/shared/selectors/EnvSelectors';
import { DELIMITER } from 'app/shared/constants/AnalyticsDelimiterConstants';
import { GA_EVENTS, GA_PREFIX } from 'app/shared/constants/AnalyticsListingModuleConstants';
import analyticsUtils from 'app/client/utils/analyticsUtils';
import propertyUtils from 'app/shared/utils/propertyUtils';
import stringHelper from 'app/shared/utils/stringHelper';
import HomeMarker from 'app/shared/images/HomeMarker.svg';

import {
    StyledRelativeGrid,
    StyledCompsSummaryContainer,
    StyledIconExternal,
} from './StyledComponents';

const pmrResultsBaseUrl = 'https://www.zillow.com/rental-manager/price-my-rental/results/';

const Comps = ({
    listingCompsModel,
    status,
}) => {
    const dispatch = useDispatch();
    const gmapsAPIKey = useSelector(state => EnvSelectors.makeGmapsAPIKey(state));
    const {
        geo,
        floorplans,
        similarFloorplans: {
            floorplans: compFloorplans,
            highestPriceIncluded,
            lowestPriceIncluded,
        },
    } = listingCompsModel;
    const boundingRect = getBoundingRect(geo, compFloorplans);
    const {
        address: {
            street,
            city,
            state,
            zip,
        },
    } = floorplans[0];

    const contentComps = `${compFloorplans.length} comparable properties`;
    const contentSummary = `listed nearby for $${lowestPriceIncluded} - $${highestPriceIncluded}`;
    const { formattedKebobCase: pmrResultsAddress } = propertyUtils.formatAddress({
        city,
        state,
        street,
        zip,
    });
    const pmrResultsLink = `${pmrResultsBaseUrl}${pmrResultsAddress}/`;

    const handleCompareToMyPropertyClick = () => {
        const { VIEW_COMPARABLES_CLICK } = GA_EVENTS;
        const { VIEW_COMPARABLES } = GA_PREFIX;

        dispatch(analyticsUtils.track.GAEventV2({
            ...VIEW_COMPARABLES_CLICK,
            action: `${VIEW_COMPARABLES}${DELIMITER}${stringHelper.startCaseRemoveSpace(status)}`,
        }));
    };

    return (
        <Flex flexGrow="1" className="Comps">
            <Grid display="grid" gap="sm" gridTemplateColumns="50% 50%">
                <StyledRelativeGrid>
                    <ZillowMapControl
                        apiKey={gmapsAPIKey}
                        id="comps-map"
                        boundingRect={boundingRect}
                        freezeMap={true}
                        mapType="roadmap"
                    >
                        <ZillowMapLayer>
                            {compFloorplans.map(comp => (
                                <PropertyMarker
                                    northSouthLat={comp.geo.lat}
                                    eastWestLng={comp.geo.lon}
                                    statusType={comp.active ? 'FOR_RENT' : 'OTHER'}
                                    showDot={true}
                                />
                            ))}
                            <div northSouthLat={geo.lat} eastWestLng={geo.lon}>
                                <Image src={HomeMarker} />
                            </div>
                        </ZillowMapLayer>
                    </ZillowMapControl>
                </StyledRelativeGrid>
                <Grid>
                    <StyledCompsSummaryContainer>
                        <Text as="div" fontType="bodyHeading">{contentComps}</Text>
                        <Text as="div">{contentSummary}</Text>
                    </StyledCompsSummaryContainer>
                </Grid>
            </Grid>
            <Spacer marginTop="sm">
                <Center>
                    <TextButton
                        as="a"
                        href={pmrResultsLink}
                        target="_blank"
                        onClick={handleCompareToMyPropertyClick}
                    >
                        Compare to my property
                        <StyledIconExternal />
                    </TextButton>
                </Center>
            </Spacer>
        </Flex>
    );
};

Comps.propTypes = {
    listingCompsModel: PropTypes.object.isRequired,
    status: PropTypes.string.isRequired,
};

Comps.defaultProps = {};

export default Comps;
